import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Step,
  StepLabel,
  Stepper,
} from '@mui/material';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import * as Yup from 'yup';
import { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { FilePond } from 'react-filepond';
import 'filepond/dist/filepond.min.css';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { addDays } from 'date-fns';
import { Close, HelpOutline } from '@mui/icons-material';
import { baseUrl } from '../../api/api';
import { FilePondFile } from 'filepond';
import { listings, HomeWarrantyType, LoanType } from '@prisma/client';
import BuyingPower from './BuyingPower';
import useIsWaitlisted from '../../hooks/useIsWaitlisted';
import WaitlistNotice from '../WaitlistNotice/WaitlistNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useAppDispatch } from '../../store/store';
import { fetchOffers } from '../../store/slices/offers';
import { matchIsValidTel, MuiTelInput } from 'mui-tel-input';
import { fetchUploads } from '../../store/slices/uploads';
import BuyerOfferSuccess from './components/BuyerOfferSuccess';
import offersService from '../../api/services/offers';
import marketAnalysisService, {
  OfferGuidance,
} from '../../api/services/marketAnalysis';
import { CustomTooltip } from '../../components/CustomTooltip/CustomTooltip';
import { getStorage } from '../../utils/localOrSessionStorage';
import Grid2 from '@mui/material/Unstable_Grid2';
import BrokerFeePreview, {
  FeePreviewPaymentMethod,
  FeePreviewInspectionMode,
} from '../BrokerFeePreview';
import OfferReasoningStep from './components/OfferReasoningStep';
import HomeSaleContingencyFields, {
  homeSaleContingencyInitialValues,
  homeSaleContingencySchema,
  toHomeSaleContingencyPayload,
} from './components/HomeSaleContingencyFields';
import {
  OfferScenario,
  OfferScenarioTerms,
  ScenarioKey,
} from '../../api/services/offerReasoning';
import {
  OFFER_WIZARD_STEPS,
  getOfferSubmitErrorMessage,
  getOfferSubmitIssues,
} from './offerSubmitIssues';

const toFeePreviewPaymentMethod = (
  value: string
): FeePreviewPaymentMethod => {
  if (value === 'Financed' || value === 'VA' || value === 'Cash') return value;
  return '';
};

const toFeePreviewInspectionMode = (
  value: string
): FeePreviewInspectionMode => {
  if (value === 'required' || value === 'informational') return value;
  return 'none';
};

interface Props {
  listing: listings;
  /**
   * Seeds the offer price when the wizard is opened from a surface that already
   * has a number in mind (the market analysis "Draft an offer" CTA). Falls back
   * to the list price.
   */
  initialPrice?: number;
}

const validationSchema = Yup.object({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  phone: Yup.string()
    .required('Phone number is required')
    .test('is-valid-tel', 'Phone number is invalid', (value) =>
      matchIsValidTel(value)
    ),
  email: Yup.string()
    .email('Invalid email format')
    .required('Email is required'),
  price: Yup.number()
    .required('Offer price is required')
    .min(1, 'Offer price must be greater than 0')
    .typeError('Offer price must be a valid number'),
  earnestMoney: Yup.number()
    .required('Earnest money is required')
    .typeError('Earnest money must be a valid number'),
  concessions: Yup.number()
    .required('Concessions amount is required')
    .typeError('Concessions must be a valid number'),
  paymentMethod: Yup.string().required('Please select a payment method'),
  closingDate: Yup.date()
    .required('Closing date is required')
    .typeError('Closing date must be a valid date'),
  inspection: Yup.string().required('Please specify inspection preference'),
  inspectionDays: Yup.number().when('inspection', {
    is: (val: string) => val === 'required' || val === 'informational',
    then: (schema) =>
      schema
        .required('Inspection days are required when inspection is selected')
        .min(1, 'Inspection days must be at least 1')
        .max(30, 'Inspection days must not exceed 30')
        .typeError('Inspection days must be a valid number'),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  loan: Yup.number().when('paymentMethod', {
    is: (val: string) => val === 'Financed' || val === 'VA',
    then: (schema) =>
      schema
        .required('Loan amount is required when financing')
        .min(1, 'Loan amount must be at least $1')
        .max(Yup.ref('price'), 'Loan amount cannot exceed offer price'),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  homeWarrantyType: Yup.string().required(
    'Please specify home warranty preference'
  ),
  ...homeSaleContingencySchema,
});

const getInspectionDays = (inspection: string, inspectionDays: number) => {
  if (inspection === 'required') return inspectionDays;
  if (inspection === 'informational') return 0 - inspectionDays;
  return null;
};

const BuyerOfferModal = NiceModal.create<Props>((props) => {
  const modal = useModal();
  const { listing, initialPrice } = props;
  const listingId = listing.id;
  const [success, setSuccess] = useState(false);
  const token = getStorage().getItem('jwtToken');
  const sessionId = useRef<string>(crypto.randomUUID());
  const { currentUser } = useAuth();
  const isWaitlisted = useIsWaitlisted();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useAppDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const [pondFiles, setPondFiles] = useState<FilePondFile[]>([]);
  const [selectedScenario, setSelectedScenario] = useState<ScenarioKey | null>(
    null
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [revaGuidance, setRevaGuidance] = useState<OfferGuidance | null>(null);
  const priceTouchedByBuyer = useRef(false);
  const steps = OFFER_WIZARD_STEPS;

  const formik = useFormik({
    initialValues: {
      email: currentUser?.email ?? '',
      firstName: currentUser?.firstName ?? '',
      lastName: currentUser?.lastName ?? '',
      phone: currentUser?.phone ?? '',
      buyerFirstName: currentUser?.firstName ?? '',
      buyerLastName: currentUser?.lastName ?? '',
      secondBuyerName: '',
      secondBuyerEmail: '',
      price: (initialPrice && initialPrice > 0
        ? initialPrice
        : listing.price
      ).toString(),
      paymentMethod: '',
      loanType: null as LoanType | null,
      files: [] as FilePondFile[],
      earnestMoney: 1000,
      closingDate: addDays(new Date(), 30),
      concessions: 0,
      inspection: 'required',
      inspectionDays: 7,
      appliances: true,
      additionalTerms: '',
      contingent: false,
      ...homeSaleContingencyInitialValues,
      loan: 0,
      expiresOn: addDays(new Date(), 4),
      termiteInspection: false,
      termiteBond: false,
      sewerInspection: false,
      newSurvey: false,
      homeWarrantyType: HomeWarrantyType.NONE,
    },
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      const formattedPhone = values.phone.replace(/ /g, '');
      setSubmitError(null);

      try {
        const { files, inspection, inspectionDays, loanType, ...rest } = values;
        const submittedPaymentMethod =
          values.paymentMethod === 'VA' ? 'Financed' : values.paymentMethod;
        await offersService.post(
          {
            ...rest,
            ...toHomeSaleContingencyPayload(values),
            paymentMethod: submittedPaymentMethod,
            buyerFirstName: values.firstName,
            buyerLastName: values.lastName,
            sessionId: sessionId.current,
            price: parseInt(values.price),
            inspectionPeriod: getInspectionDays(inspection, inspectionDays),
            bagentComm: 0,
            earnestMoney: values.earnestMoney,
            buyerPhone: formattedPhone,
            loanType: loanType || null,
            listings: {
              connect: {
                id: listingId,
              },
            },
          },
          true
        );
        setSuccess(true);
        dispatch(fetchOffers());
        dispatch(fetchUploads({ listingId }));
      } catch (e) {
        setSubmitError(getOfferSubmitErrorMessage(e));
      }
    },
  });

  // Formik touches every field on a submit attempt, so once the buyer has
  // pressed Submit, any remaining error is one they need to see and fix.
  const submitIssues =
    formik.submitCount > 0 ? getOfferSubmitIssues(formik.errors) : [];
  const submitFeedbackRef = useRef<HTMLDivElement>(null);
  const hasSubmitFeedback = submitIssues.length > 0 || Boolean(submitError);

  useEffect(() => {
    if (hasSubmitFeedback) {
      submitFeedbackRef.current?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [formik.submitCount, submitError, hasSubmitFeedback]);

  // Reva's recommended price comes from the same comps as the market analysis
  // card. A price handed in by the opener always wins; otherwise the
  // recommendation seeds the field until the buyer or a scenario sets it.
  const { setFieldValue } = formik;
  useEffect(() => {
    let cancelled = false;
    marketAnalysisService
      .find(listingId)
      .then((data) => {
        if (cancelled || !data.available || !data.offerGuidance) return;
        setRevaGuidance(data.offerGuidance);
        const hasOpenerPrice = Boolean(initialPrice && initialPrice > 0);
        if (hasOpenerPrice || priceTouchedByBuyer.current) return;
        setFieldValue('price', data.offerGuidance.suggested.toString(), false);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId]);

  const formatCurrency = (value: string | number) => {
    if (!value) return '0';
    // Remove any existing commas first
    const num =
      typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
    return num.toLocaleString('en-US');
  };

  const handleCurrencyChange =
    (fieldName: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      if (fieldName === 'price') priceTouchedByBuyer.current = true;
      // Remove any non-numeric characters except decimal point
      const cleanedValue = e.target.value.replace(/[^0-9.]/g, '');
      const rawValue = cleanedValue === '' ? 0 : parseInt(cleanedValue);
      formik.setFieldValue(fieldName, rawValue);
    };

  /**
   * Copy a scenario's terms into the wizard. Every field stays editable on the
   * later steps — this only sets the starting values.
   */
  const applyScenarioTerms = (terms: OfferScenarioTerms) => {
    priceTouchedByBuyer.current = true;
    formik.setFieldValue('price', terms.price.toString());
    formik.setFieldValue('earnestMoney', terms.earnestMoney);
    formik.setFieldValue('inspection', 'required');
    formik.setFieldValue('inspectionDays', terms.inspectionPeriod);
    formik.setFieldValue('paymentMethod', terms.paymentMethod);
    formik.setFieldValue('concessions', terms.concessions);
    formik.setFieldValue('contingent', terms.contingent);
    formik.setFieldValue('closingDate', addDays(new Date(), terms.closingDays));
  };

  const handleSelectScenario = (scenario: OfferScenario) => {
    setSelectedScenario(scenario.key);
    applyScenarioTerms(scenario.terms);
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    goToStep(activeStep - 1);
  };

  const goToStep = (step: number) => {
    setActiveStep(step);
    const existingFiles = formik.values.files.map((file) => ({
      source: file.serverId,
      options: {
        type: 'local',
        file: {
          name: file.filename,
          size: file.fileSize,
          type: file.fileType,
        },
      },
    }));
    setPondFiles(existingFiles as any);
  };

  useEffect(() => {
    if (activeStep > 0) {
      const dialogContent = document.querySelector('.MuiDialogContent-root');
      if (dialogContent) {
        dialogContent.scrollTo(0, 0);
      }
    }
  }, [activeStep]);

  if (isWaitlisted) {
    return (
      <Dialog
        open={modal.visible}
        onClose={() => modal.remove()}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogContent>
          <WaitlistNotice variant="compact" />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={modal.visible}
      onClose={() => modal.remove()}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
        >
          <Typography variant="h6" fontWeight={600}>
            Submit Offer for Review by Homepoint
          </Typography>
          <IconButton onClick={() => modal.remove()}>
            <Close />
          </IconButton>
        </Stack>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Homepoint will review your offer and reach back out to you to discuss
          and refine the offer.
        </Typography>
      </DialogTitle>
      <DialogContent>
        {success ? (
          <BuyerOfferSuccess propertyAddress={listing.address} />
        ) : (
          <>
            <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {(() => {
              switch (activeStep) {
                case 0:
                  return (
                    <OfferReasoningStep
                      listing={listing}
                      selectedKey={selectedScenario}
                      onSelectScenario={handleSelectScenario}
                      onContinue={handleNext}
                    />
                  );
                case 1:
                  return (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body1" fontWeight={500}>
                          Documents
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <FilePond
                          files={pondFiles as any}
                          onupdatefiles={(fileItems) => {
                            setPondFiles(fileItems);
                            formik.setFieldValue('files', fileItems);
                          }}
                          allowMultiple={true}
                          maxFiles={5}
                          allowRevert={false}
                          credits={false}
                          server={{
                            process: (
                              _fieldName,
                              file,
                              _metadata,
                              load,
                              error,
                              progress,
                              abort
                            ) => {
                              const formData = new FormData();
                              formData.append('listingId', listingId);
                              formData.append('type', 'OFFER_DOC');
                              formData.append('sessionId', sessionId.current);
                              formData.append('file', file);

                              const request = new XMLHttpRequest();
                              request.open('POST', `${baseUrl}/uploads`);
                              request.setRequestHeader(
                                'Authorization',
                                `Bearer ${token}`
                              );

                              request.upload.onprogress = (e) => {
                                progress(e.lengthComputable, e.loaded, e.total);
                              };

                              request.onload = function () {
                                if (
                                  request.status >= 200 &&
                                  request.status < 300
                                ) {
                                  load(request.responseText);
                                } else {
                                  error('Upload failed');
                                }
                              };

                              request.send(formData);

                              return {
                                abort: () => {
                                  request.abort();
                                  abort();
                                },
                              };
                            },
                          }}
                          name="files"
                          // eslint-disable-next-line max-len
                          labelIdle='Drag & Drop your pre-approval, proof of funds, etc., or <span class="filepond--label-action">Browse</span>'
                        />
                      </Grid>
                    </Grid>
                  );

                case 2:
                  return (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body1" fontWeight={500}>
                          Your Information
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          name="firstName"
                          label="First Name"
                          value={formik.values.firstName}
                          onChange={formik.handleChange}
                          fullWidth
                          required
                          helperText={
                            formik.touched.firstName && formik.errors.firstName
                          }
                          error={
                            formik.touched.firstName &&
                            Boolean(formik.errors.firstName)
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          name="lastName"
                          label="Last Name"
                          value={formik.values.lastName}
                          onChange={formik.handleChange}
                          fullWidth
                          required
                          helperText={
                            formik.touched.lastName && formik.errors.lastName
                          }
                          error={
                            formik.touched.lastName &&
                            Boolean(formik.errors.lastName)
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          name="email"
                          label="Email"
                          value={formik.values.email}
                          onChange={formik.handleChange}
                          fullWidth
                          required
                          helperText={
                            formik.touched.email && formik.errors.email
                          }
                          error={
                            formik.touched.email && Boolean(formik.errors.email)
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <MuiTelInput
                          value={formik.values.phone}
                          onChange={(value) =>
                            formik.setFieldValue('phone', value)
                          }
                          onlyCountries={['US']}
                          defaultCountry="US"
                          label="Phone Number"
                          forceCallingCode={true}
                          fullWidth
                          helperText={
                            formik.touched.phone && formik.errors.phone
                          }
                          error={
                            formik.touched.phone && Boolean(formik.errors.phone)
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          name="secondBuyerName"
                          label="Second Buyer Name (Optional)"
                          value={formik.values.secondBuyerName}
                          onChange={formik.handleChange}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          name="secondBuyerEmail"
                          label="Second Buyer Email (Optional)"
                          value={formik.values.secondBuyerEmail}
                          onChange={formik.handleChange}
                          fullWidth
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="body1" fontWeight={500}>
                          Offer Details
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControl>
                          <FormLabel id="paymentMethod">
                            Payment Method
                          </FormLabel>
                          <RadioGroup
                            row
                            aria-labelledby="paymentMethod"
                            name="paymentMethod"
                            value={formik.values.paymentMethod}
                            onChange={formik.handleChange}
                          >
                            <FormControlLabel
                              value={'Financed'}
                              control={<Radio />}
                              label="Financed"
                            />
                            <FormControlLabel
                              value={'VA'}
                              control={<Radio />}
                              label="VA Loan"
                            />
                            <FormControlLabel
                              value={'Cash'}
                              control={<Radio />}
                              label="Cash"
                            />
                          </RadioGroup>
                          {formik.touched.paymentMethod &&
                            formik.errors.paymentMethod && (
                              <Typography variant="body2" color="error">
                                {formik.errors.paymentMethod}
                              </Typography>
                            )}
                        </FormControl>
                      </Grid>
                      {(formik.values.paymentMethod === 'Financed' ||
                        formik.values.paymentMethod === 'VA') && (
                        <>
                          <Grid item xs={6} sm={4}>
                            <TextField
                              name="loan"
                              label="Loan Amount"
                              value={formatCurrency(formik.values.loan)}
                              onChange={handleCurrencyChange('loan')}
                              type="text"
                              fullWidth
                              required
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    $
                                  </InputAdornment>
                                ),
                              }}
                              helperText={
                                formik.touched.loan && formik.errors.loan
                              }
                              error={
                                formik.touched.loan && Boolean(formik.errors.loan)
                              }
                            />
                          </Grid>
                          {formik.values.paymentMethod === 'Financed' && (
                            <Grid item xs={6} sm={4}>
                              <TextField
                                select
                                name="loanType"
                                label="Loan Type"
                                value={formik.values.loanType || ''}
                                onChange={formik.handleChange}
                                fullWidth
                              >
                                <MenuItem value="CONVENTIONAL">Conventional</MenuItem>
                                <MenuItem value="FHA">FHA</MenuItem>
                                <MenuItem value="VA">VA</MenuItem>
                              </TextField>
                            </Grid>
                          )}
                        </>
                      )}

                      <Grid item xs={12}>
                        <TextField
                          name="price"
                          label="Offer Price"
                          value={formatCurrency(formik.values.price)}
                          onChange={handleCurrencyChange('price')}
                          type="text"
                          fullWidth
                          required
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                $
                              </InputAdornment>
                            ),
                          }}
                          helperText={
                            formik.touched.price && formik.errors.price
                          }
                          error={
                            formik.touched.price && Boolean(formik.errors.price)
                          }
                        />
                        {revaGuidance && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            component="div"
                            sx={{ mt: 0.5 }}
                            data-testid="offer-price-reva-suggestion"
                          >
                            Reva suggests $
                            {formatCurrency(revaGuidance.suggested)} from recent
                            sales ($
                            {formatCurrency(revaGuidance.low)}–$
                            {formatCurrency(revaGuidance.high)})
                            {parseInt(formik.values.price) !==
                              revaGuidance.suggested && (
                              <Button
                                size="small"
                                data-testid="offer-price-use-reva"
                                sx={{
                                  ml: 0.5,
                                  minWidth: 0,
                                  p: 0,
                                  fontSize: 'inherit',
                                }}
                                onClick={() => {
                                  priceTouchedByBuyer.current = true;
                                  formik.setFieldValue(
                                    'price',
                                    revaGuidance.suggested.toString()
                                  );
                                }}
                              >
                                Use it
                              </Button>
                            )}
                          </Typography>
                        )}
                        {listing.price > 0 && (
                          <BuyingPower
                            listing={listing}
                            offerPrice={parseInt(formik.values.price)}
                            handlePriceChange={(price) =>
                              formik.setFieldValue('price', price)
                            }
                          />
                        )}
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          name="earnestMoney"
                          label="Earnest Money"
                          value={formatCurrency(formik.values.earnestMoney)}
                          onChange={handleCurrencyChange('earnestMoney')}
                          type="text"
                          fullWidth
                          required
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                $
                              </InputAdornment>
                            ),
                          }}
                          helperText={
                            formik.touched.earnestMoney &&
                            formik.errors.earnestMoney
                              ? formik.errors.earnestMoney
                              : // eslint-disable-next-line max-len
                                'Earnest money is a deposit to show the seller you are serious about purchasing the property.'
                          }
                          error={
                            formik.touched.earnestMoney &&
                            Boolean(formik.errors.earnestMoney)
                          }
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DatePicker
                            label="Closing Date"
                            value={formik.values.closingDate}
                            onChange={(value) =>
                              formik.setFieldValue('closingDate', value)
                            }
                            sx={{ width: '100%' }}
                            slotProps={{ textField: { required: true } }}
                          />
                        </LocalizationProvider>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="body1" fontWeight={500}>
                          Additional Terms
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <FormControl>
                          <FormLabel id="appliances">
                            Include Appliances
                          </FormLabel>
                          <RadioGroup
                            row
                            aria-labelledby="appliances"
                            name="appliances"
                            value={formik.values.appliances.toString()}
                            onChange={(e) => {
                              formik.setFieldValue(
                                'appliances',
                                e.target.value === 'true'
                              );
                            }}
                          >
                            <FormControlLabel
                              value="true"
                              control={<Radio />}
                              label="Yes"
                            />
                            <FormControlLabel
                              value="false"
                              control={<Radio />}
                              label="No"
                            />
                          </RadioGroup>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <FormControl>
                          <FormLabel id="contingent">
                            Contingent on Sale of Current Property
                          </FormLabel>
                          <RadioGroup
                            row
                            aria-labelledby="contingent"
                            name="contingent"
                            value={formik.values.contingent.toString()}
                            onChange={(e) => {
                              formik.setFieldValue(
                                'contingent',
                                e.target.value === 'true'
                              );
                            }}
                          >
                            <FormControlLabel
                              value="true"
                              control={<Radio />}
                              label="Yes"
                            />
                            <FormControlLabel
                              value="false"
                              control={<Radio />}
                              label="No"
                            />
                          </RadioGroup>
                        </FormControl>
                      </Grid>

                      {formik.values.contingent && (
                        <Grid item xs={12}>
                          <HomeSaleContingencyFields formik={formik} />
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        <TextField
                          name="additionalTerms"
                          label="Additional Terms or Comments"
                          value={formik.values.additionalTerms}
                          onChange={formik.handleChange}
                          multiline
                          rows={4}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  );

                case 3:
                  return (
                    <>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="h6" fontWeight={500}>
                          Inspection Requirements
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Specify your inspection requirements for this
                          property.
                        </Typography>
                        <FormControl>
                          <RadioGroup
                            aria-labelledby="inspection"
                            name="inspection"
                            value={formik.values.inspection}
                            onChange={formik.handleChange}
                          >
                            <FormControlLabel
                              value="required"
                              control={<Radio />}
                              label="Yes, with right to request repairs"
                              sx={{ mb: 0.5 }}
                            />
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              sx={{ ml: 4, mb: 1, display: 'block' }}
                            >
                              You can inspect and request repairs. Seller must
                              respond to repair requests.
                            </Typography>

                            <FormControlLabel
                              value="informational"
                              control={<Radio />}
                              label="Yes, for information only"
                              sx={{ mb: 0.5 }}
                            />
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              sx={{ ml: 4, mb: 1, display: 'block' }}
                            >
                              You can inspect but cannot request repairs. You
                              can still cancel based on findings.
                            </Typography>

                            <FormControlLabel
                              value="none"
                              control={<Radio />}
                              label="No inspection"
                            />
                          </RadioGroup>
                          {formik.touched.inspection &&
                            formik.errors.inspection && (
                              <Typography variant="body2" color="error">
                                {formik.errors.inspection}
                              </Typography>
                            )}
                        </FormControl>
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          name="inspectionDays"
                          label="Inspection Days"
                          value={formik.values.inspectionDays}
                          disabled={formik.values.inspection === 'none'}
                          onChange={formik.handleChange}
                          type="number"
                          fullWidth
                          required={formik.values.inspection !== 'none'}
                          error={
                            formik.touched.inspectionDays &&
                            Boolean(formik.errors.inspectionDays)
                          }
                          helperText={
                            formik.touched.inspectionDays &&
                            formik.errors.inspectionDays
                          }
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Typography
                          variant="body1"
                          fontWeight={500}
                          sx={{ mt: 2 }}
                        >
                          Additional Inspections
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Select any additional inspections you would like to
                          perform and require as part of the offer.
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <FormControl>
                          <FormLabel
                            id="woodInfestationReport"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            Wood Infestation Report
                            <CustomTooltip
                              // eslint-disable-next-line max-len
                              title="A professional inspection that identifies any current or previous termite or wood-destroying insect activity, damage, and conditions conducive to infestation. Often required by lenders."
                              width="300px"
                              arrow
                            >
                              <HelpOutline fontSize="small" color="action" />
                            </CustomTooltip>
                          </FormLabel>
                          <RadioGroup
                            row
                            aria-labelledby="woodInfestationReport"
                            name="woodInfestationReport"
                            // eslint-disable-next-line max-len
                            value={formik.values.termiteInspection.toString()}
                            onChange={(e) => {
                              formik.setFieldValue(
                                'termiteInspection',
                                e.target.value === 'true'
                              );
                            }}
                          >
                            <FormControlLabel
                              value="true"
                              control={<Radio />}
                              label="Yes"
                            />
                            <FormControlLabel
                              value="false"
                              control={<Radio />}
                              label="No"
                            />
                          </RadioGroup>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <FormControl>
                          <FormLabel
                            id="termiteBond"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            Termite Agreement
                            <CustomTooltip
                              // eslint-disable-next-line max-len
                              title="A service contract that provides ongoing protection against termites, including regular inspections and treatment if needed. This is different from a one-time inspection."
                              width="300px"
                              arrow
                            >
                              <HelpOutline fontSize="small" color="action" />
                            </CustomTooltip>
                          </FormLabel>
                          <RadioGroup
                            row
                            aria-labelledby="termiteBond"
                            name="termiteBond"
                            value={formik.values.termiteBond.toString()}
                            onChange={(e) => {
                              formik.setFieldValue(
                                'termiteBond',
                                e.target.value === 'true'
                              );
                            }}
                          >
                            <FormControlLabel
                              value="true"
                              control={<Radio />}
                              label="Yes"
                            />
                            <FormControlLabel
                              value="false"
                              control={<Radio />}
                              label="No"
                            />
                          </RadioGroup>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <FormControl>
                          <FormLabel
                            id="sewerInspection"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            Sewer/Septic Inspection
                            <CustomTooltip
                              // eslint-disable-next-line max-len
                              title="An inspection of the property's waste disposal system. For homes with city sewer connections, this checks for line blockages or damage. For septic systems, it evaluates the tank, drain field, and overall system function."
                              width="300px"
                              arrow
                            >
                              <HelpOutline fontSize="small" color="action" />
                            </CustomTooltip>
                          </FormLabel>
                          <RadioGroup
                            row
                            aria-labelledby="sewerInspection"
                            name="sewerInspection"
                            // eslint-disable-next-line max-len
                            value={formik.values.sewerInspection.toString()}
                            onChange={(e) => {
                              formik.setFieldValue(
                                'sewerInspection',
                                e.target.value === 'true'
                              );
                            }}
                          >
                            <FormControlLabel
                              value="true"
                              control={<Radio />}
                              label="Yes"
                            />
                            <FormControlLabel
                              value="false"
                              control={<Radio />}
                              label="No"
                            />
                          </RadioGroup>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <FormControl>
                          <FormLabel
                            id="newSurvey"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            New Survey
                            <CustomTooltip
                              // eslint-disable-next-line max-len
                              title="A professional measurement and mapping of the property boundaries, structures, and improvements. Helps identify encroachments, easements, and boundary disputes. Often required by title companies and lenders."
                              width="300px"
                              arrow
                            >
                              <HelpOutline fontSize="small" color="action" />
                            </CustomTooltip>
                          </FormLabel>
                          <RadioGroup
                            row
                            aria-labelledby="newSurvey"
                            name="newSurvey"
                            value={formik.values.newSurvey.toString()}
                            onChange={(e) => {
                              formik.setFieldValue(
                                'newSurvey',
                                e.target.value === 'true'
                              );
                            }}
                          >
                            <FormControlLabel
                              value="true"
                              control={<Radio />}
                              label="Yes"
                            />
                            <FormControlLabel
                              value="false"
                              control={<Radio />}
                              label="No"
                            />
                          </RadioGroup>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={
                            formik.touched.homeWarrantyType &&
                            Boolean(formik.errors.homeWarrantyType)
                          }
                        >
                          <FormLabel
                            id="homeWarrantyType"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            Home Warranty
                            <CustomTooltip
                              // eslint-disable-next-line max-len
                              title="A home warranty provides coverage for repairs or replacements of home systems and appliances due to normal wear and tear."
                              width="300px"
                              arrow
                            >
                              <HelpOutline fontSize="small" color="action" />
                            </CustomTooltip>
                          </FormLabel>
                          <RadioGroup
                            aria-labelledby="homeWarrantyType"
                            name="homeWarrantyType"
                            value={formik.values.homeWarrantyType}
                            onChange={formik.handleChange}
                          >
                            <FormControlLabel
                              value={HomeWarrantyType.NONE}
                              control={<Radio />}
                              label="No home warranty required"
                            />
                            <FormControlLabel
                              value={HomeWarrantyType.BUYER}
                              control={<Radio />}
                              label="Yes, I will pay for home warranty"
                            />
                            <FormControlLabel
                              value={HomeWarrantyType.SELLER}
                              control={<Radio />}
                              // eslint-disable-next-line max-len
                              label="Yes, request seller to pay for home warranty"
                            />
                          </RadioGroup>
                          {formik.touched.homeWarrantyType &&
                            formik.errors.homeWarrantyType && (
                              <Typography variant="body2" color="error">
                                {formik.errors.homeWarrantyType}
                              </Typography>
                            )}
                        </FormControl>
                      </Grid>
                    </Grid>
                    <Grid2 container spacing={2} sx={{ mt: 0 }}>
                      <Grid2 xs={12}>
                        <BrokerFeePreview
                          price={Number(formik.values.price) || 0}
                          paymentMethod={toFeePreviewPaymentMethod(
                            formik.values.paymentMethod
                          )}
                          inspection={toFeePreviewInspectionMode(
                            formik.values.inspection
                          )}
                          hasHomeSaleContingency={formik.values.contingent}
                        />
                      </Grid2>
                    </Grid2>
                    </>
                  );

                default:
                  return null;
              }
            })()}

            <div ref={submitFeedbackRef}>
              {activeStep === steps.length - 1 && submitIssues.length > 0 && (
                <Alert
                  severity="error"
                  sx={{ mt: 3 }}
                  data-testid="offer-submit-issues"
                >
                  <AlertTitle>
                    Fix {submitIssues.length === 1 ? 'this' : 'these'} before
                    submitting
                  </AlertTitle>
                  <Stack component="ul" spacing={1} sx={{ m: 0, pl: 2 }}>
                    {submitIssues.map((issue) => (
                      <li key={issue.field}>
                        <Stack
                          direction={isMobile ? 'column' : 'row'}
                          alignItems={isMobile ? 'flex-start' : 'center'}
                          spacing={isMobile ? 0 : 1}
                        >
                          <Typography variant="body2">
                            <strong>{issue.label}:</strong> {issue.message}
                          </Typography>
                          {issue.step !== activeStep && (
                            <Button
                              size="small"
                              color="error"
                              onClick={() => goToStep(issue.step)}
                              sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                            >
                              Fix on {issue.stepLabel} step
                            </Button>
                          )}
                        </Stack>
                      </li>
                    ))}
                  </Stack>
                </Alert>
              )}

              {activeStep === steps.length - 1 && submitError && (
                <Alert
                  severity="error"
                  sx={{ mt: 3 }}
                  data-testid="offer-submit-error"
                  onClose={() => setSubmitError(null)}
                >
                  {submitError}
                </Alert>
              )}
            </div>

            {/* The reasoning step carries its own full-width CTA. */}
            {activeStep > 0 && (
              <Stack
                direction="row"
                justifyContent={'space-between'}
                pt={2}
                spacing={2}
              >
                {!isMobile && (
                  <img
                    // eslint-disable-next-line max-len
                    src="https://www.nar.realtor/sites/default/files/downloadable/equal-housing-opportunity-logo-1200w.jpg"
                    width="50"
                    alt="Equal Housing Opportunity"
                  />
                )}
                <Stack
                  direction={isMobile ? 'column' : 'row'}
                  spacing={2}
                  width={'100%'}
                  justifyContent={'flex-end'}
                >
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    fullWidth={isMobile}
                  >
                    Back
                  </Button>
                  {activeStep === steps.length - 1 ? (
                    <Button
                      variant="contained"
                      onClick={() => formik.handleSubmit()}
                      disabled={formik.isSubmitting}
                      fullWidth={isMobile}
                    >
                      {formik.isSubmitting
                        ? 'Submitting…'
                        : 'Submit for Review'}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      fullWidth={isMobile}
                    >
                      Next
                    </Button>
                  )}
                </Stack>
              </Stack>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
});

export default BuyerOfferModal;
